<script lang="ts">
	// The first screen a till ever shows. Without a till cookie there is
	// no account and no shop, so nothing else can render.
	//
	// The pairing code uses a 32-symbol alphabet with no 0, O, 1, or I, because
	// an owner reads it aloud from a dashboard to whoever stands at the counter.
	// Once it is accepted the screen says which restaurant, shop and till this
	// now is, and waits for Continue, so a till paired to the wrong shop is
	// caught before anyone sells on it.

	import { startOnboarding } from '../till/setup.svelte';
	import Button from '../components/Button.svelte';
	import BrandMark from '../components/BrandMark.svelte';
	import Chip from '../components/Chip.svelte';
	import Keypad from '../components/Keypad.svelte';
	import PasscodeDots from '../components/PasscodeDots.svelte';
	import { message, type Language } from '../i18n/messages';
	import { api } from '../api/client';
	import type { components } from '../api/types';

	type Identity = components['schemas']['TillIdentityBody'];

	interface Props {
		language: Language;
		onPaired?: (identity: Identity) => void;
	}

	let { language, onPaired }: Props = $props();

	const codeLength = 8;

	// How long a check runs before the screen says it is pairing.
	const slowMs = 300;

	let code = $state('');
	let working = $state(false);
	let slow = $state(false);
	let failure = $state('');
	let locked = $state(false);
	let onboardingStarted = $state(false);
	let paired = $state<Identity | null>(null);
	const complete = $derived(code.length === codeLength);

	function append(symbol: string) {
		if (complete || working) return;
		code += symbol;
		// The pad submits itself at full length. The length is fixed, so there
		// is nothing for a confirm step to add.
		if (code.length === codeLength) void submit();
	}

	async function submit() {
		working = true;
		failure = '';
		locked = false;
		const timer = setTimeout(() => (slow = true), slowMs);
		try {
			const { data, error, response } = await api.POST('/api/till/pair', {
				body: { code },
			});
			if (error !== undefined || !data) {
				// Kept, and shown amber: the code was read off a phone, so the
				// likely fault is one character, and the cashier can find it.
				locked = response.status === 423;
				failure = locked ? message(language, 'pairLocked') : message(language, 'pairFailed');
				return;
			}
			paired = data;
			// The till's onboarding starts right after its pairing (UC-59); a
			// start the store did not take is asked again at Continue.
			void startOnboarding().then((started) => (onboardingStarted = started));
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			clearTimeout(timer);
			slow = false;
			working = false;
		}
	}

	function backspace() {
		failure = '';
		locked = false;
		code = code.slice(0, -1);
	}
</script>

<section class="pair" data-testid="screen-pair">
	<div class="heading">
		<BrandMark size={26} />
		<h1 data-testid="pair-title">{message(language, 'pairTitle')}</h1>
		<p class="instruction">{message(language, 'pairInstruction')}</p>
	</div>

	{#if paired}
		<div class="paired" data-testid="pair-success">
			<div>
				<Chip text={message(language, 'pairedAs')} tone="ok" size="md" />
			</div>
			<div class="names">
				<p class="account">{paired.account}</p>
				<p class="where">
					{paired.shop} · {paired.till}{paired.hasDrawer
						? ` · ${message(language, 'pairHasDrawer')}`
						: ''}
				</p>
			</div>
			{#if onboardingStarted}<p class="where" data-testid="pair-next-setup">
					{message(language, 'pairNextSetup')}
				</p>{/if}
			<Button
				testId="pair-continue"
				label={message(language, 'pairContinue')}
				wide
				onPress={() => {
					if (!onboardingStarted) void startOnboarding();
					if (paired) onPaired?.(paired);
				}}
			/>
		</div>
	{:else}
		<PasscodeDots
			reveal
			value={code}
			state={locked ? 'locked' : failure ? 'error' : 'idle'}
			message={failure || (slow ? message(language, 'pairChecking') : '')}
			dotsTestId="pair-code"
			messageTestId="pair-error"
		/>

		<Keypad
			testId="pair"
			layout="pairing"
			label={message(language, 'pairCodeLabel')}
			deleteLabel={message(language, 'backspace')}
			disabled={working}
			canType={!complete && !locked}
			canDelete={code.length > 0}
			onKey={append}
			onBackspace={backspace}
		/>
	{/if}
</section>

<style>
	.pair {
		--ctl-min: var(--tap-min);
		min-block-size: 100dvh;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--space-7);
		padding: var(--space-7);
		background: var(--surface-page);
	}

	.heading {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-3);
		text-align: center;
	}

	h1 {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
		letter-spacing: var(--tracking-snug);
	}

	.instruction {
		margin: 0;
		font-size: var(--text-base);
		color: var(--text-body);
	}

	.paired {
		inline-size: 100%;
		max-inline-size: 440px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.names {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.account {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
	}

	.where {
		margin: 0;
		font-size: var(--text-base);
		color: var(--text-body);
	}
</style>
